import type { SivVehicle } from 'korint-sdk';
import type { Field, VehicleIdentityFieldKey, VehicleInputTab } from 'korint-sdk/react';
import dayjs from 'dayjs';
import { CarIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ControllerRenderProps, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '../../forms/input';
import { RequiredAsterisk } from '../../forms/RequiredAsterisk/RequiredAsterisk';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../forms/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { VehicleBrandFormField } from '../VehicleBrandFormField';
import { VehicleModelFormField } from '../VehicleModelFormField';
import { VehicleVersionFormField } from '../VehicleVersionFormField';
import { VehicleRegistrationNumberInput } from '../vehicle-registration-number-input';
import { VehicleComparisonCard, type VehicleComparisonRow } from './VehicleComparisonCard';
import { DEFAULT_VEHICLE_IDENTITY_FIELDS, VEHICLE_SELECTION_RESOLVED_FIELD } from './constants';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Alert, AlertDescription } from '@folio/components/ui/alert';
import { useFirmType } from '@folio/hooks/use-firm-type';

type VehicleInputField = Extract<Field, { type: 'vehicleInput' }>;

const DEFAULT_TABS: Array<VehicleInputTab> = ['plate', 'brandModel', 'manual'];
// Non-identity fields carried in the context snapshot so "Conserver" also restores the
// version-derived specs.
const SNAPSHOT_EXTRA_KEYS = ['fiscalPower', 'numberOfSeats'] as const;

type Snapshot = Record<string, unknown>;

function toDateInputValue(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

function formatIdentityValue(key: VehicleIdentityFieldKey, value: unknown): string {
  if (value == null || value === '') return '';
  if (key === 'firstCirculationDate') {
    const parsed = dayjs(value as string);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(value);
  }
  return String(value);
}

function LabeledField({
  name,
  label,
  required,
  children,
}: {
  name: string;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  const form = useFormContext();
  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem>
          <FormLabel>
            <span className="flex">
              {label}
              {required && <RequiredAsterisk />}
            </span>
          </FormLabel>
          <FormControl>{children}</FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// A native date input emits transient leading-zero years (0002, 0020…) while the year is typed;
// commit only a full 4-digit year (>= 1000). `min` is dropped so those partials stay < 1000
// (with a min, some browsers seed the year from it and the partials look real, e.g. 1902, 1920).
function VehicleDateInput({ field }: { field: ControllerRenderProps }) {
  const [draft, setDraft] = useState(() => toDateInputValue(field.value));
  useEffect(() => {
    setDraft(toDateInputValue(field.value));
  }, [field.value]);
  return (
    <Input
      ref={field.ref}
      name={field.name}
      type="date"
      max="9999-12-31"
      value={draft}
      onChange={event => {
        const value = event.target.value;
        setDraft(value);
        if (value === '' || Number(value.slice(0, 4)) >= 1000) {
          field.onChange(value);
        }
      }}
    />
  );
}

export function VehicleInputFormField({
  field,
  defaultTab,
  onVehicleFound,
  onActiveTabChange,
}: {
  field: VehicleInputField;
  defaultTab?: VehicleInputTab;
  onVehicleFound?: (vehicle: SivVehicle) => void;
  onActiveTabChange?: (tab: VehicleInputTab) => void;
}) {
  const { t } = useTranslation();
  const form = useFormContext();

  const compositeName = field.name;
  const basePath = compositeName.split('.').slice(0, -1).join('.');
  const scoped = useCallback((key: string) => (basePath ? `${basePath}.${key}` : key), [basePath]);

  const { firmType, user } = useFirmType();
  // Allowed while the firm loads, so a saved manual vehicle keeps its tab on first render
  const canUseManualTab =
    !field.manualTabFirmTypes ||
    !!user?.isSuperUser ||
    !firmType ||
    field.manualTabFirmTypes.includes(firmType);
  const tabs = (field.tabs ?? DEFAULT_TABS).filter(tab => tab !== 'manual' || canUseManualTab);
  const identityFields = field.identityFields ?? DEFAULT_VEHICLE_IDENTITY_FIELDS;

  const savedAsManual =
    !!field.manualFlagField && form.getValues(scoped(field.manualFlagField)) === true;
  const [activeTab, setActiveTab] = useState<VehicleInputTab>(
    savedAsManual && tabs.includes('manual')
      ? 'manual'
      : ((defaultTab && tabs.includes(defaultTab) ? defaultTab : tabs[0]) ?? 'plate'),
  );
  const [plateNotFound, setPlateNotFound] = useState(false);
  // Committed reference the working form is compared against; null until a vehicle is built.
  const [contextVehicle, setContextVehicle] = useState<Snapshot | null>(null);

  const watchedValues = form.watch(identityFields.map(scoped)) as Array<string | undefined>;
  const working = Object.fromEntries(
    identityFields.map((key, index) => [key, watchedValues[index]]),
  ) as Partial<Record<VehicleIdentityFieldKey, string>>;
  // Stable dep for the folding effect below (`working` is a fresh object each render).
  const workingKey = identityFields.map(key => working[key] ?? '').join('');

  const hasBuiltVehicle = Boolean(working.brand && working.model);

  const takeSnapshot = useCallback((): Snapshot => {
    const keys = [...identityFields, ...SNAPSHOT_EXTRA_KEYS];
    return Object.fromEntries(keys.map(key => [key, form.getValues(scoped(key))]));
  }, [form, scoped, identityFields]);

  // Diff only where the reference holds a non-empty value the working form changed. Filling a
  // still-empty reference field (e.g. the SRA version, absent from a plate search) completes it,
  // it is not a modification and shows no card.
  const hasDiff = useCallback(
    (context: Snapshot, work: Snapshot) =>
      identityFields.some(key => {
        // Normalise dates so a re-typed identical day (ISO vs YYYY-MM-DD) is not a spurious diff.
        const norm = (v: unknown) =>
          key === 'firstCirculationDate' ? toDateInputValue(v) : ((v ?? '') as string);
        const current = norm(context[key]);
        return current !== '' && current !== norm(work[key]);
      }),
    [identityFields],
  );

  const isManualTab = activeTab === 'manual';
  // Manual typing builds the vehicle keystroke by keystroke: no reference to compare against
  const showCard = !isManualTab && contextVehicle != null && hasDiff(contextVehicle, working);

  // Show the "must choose" message only after a Suivant attempt while a card is pending.
  const { submitCount } = useFormState({ control: form.control });
  const [suivantBlocked, setSuivantBlocked] = useState(false);
  const submitCountRef = useRef(submitCount);
  useEffect(() => {
    if (submitCount !== submitCountRef.current) {
      submitCountRef.current = submitCount;
      if (showCard) setSuivantBlocked(true);
    }
  }, [submitCount, showCard]);
  useEffect(() => {
    if (!showCard) setSuivantBlocked(false);
  }, [showCard]);

  // "Véhicule actuel" banner: the frozen reference if set, else the in-progress build.
  const bannerVehicle: Snapshot | Partial<Record<VehicleIdentityFieldKey, string>> | null =
    contextVehicle ?? (hasBuiltVehicle ? working : null);

  // Establish the reference the first time a vehicle is built (brand+model present), any tab/flow.
  // Uses watched values, not formState.defaultValues (unreliable under `<Form {...form}>`).
  useEffect(() => {
    if (contextVehicle == null && hasBuiltVehicle && !isManualTab) {
      setContextVehicle(takeSnapshot());
    }
  }, [contextVehicle, hasBuiltVehicle, isManualTab, takeSnapshot]);

  useEffect(() => {
    if (!tabs.includes(activeTab) && tabs[0]) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  // Fold a field the reference left empty (e.g. the picked version + its specs) into the reference,
  // so the banner reflects it with no card; a later change to a now-set field diverges → card.
  useEffect(() => {
    if (contextVehicle == null) return;
    const completions: Snapshot = {};
    for (const key of [...identityFields, ...SNAPSHOT_EXTRA_KEYS]) {
      const reference = contextVehicle[key];
      if (reference != null && reference !== '') continue;
      const current = form.getValues(scoped(key));
      if (current != null && current !== '') completions[key] = current;
    }
    if (Object.keys(completions).length > 0) {
      setContextVehicle(prev => (prev ? { ...prev, ...completions } : prev));
    }
  }, [workingKey, contextVehicle, identityFields, form, scoped]);

  const applyVehicle = useCallback(
    (vehicle: SivVehicle) => {
      // Clear the previous SRA-derived data. null clears the input; undefined is a no-op in RHF.
      form.setValue(scoped('version'), '', { shouldDirty: true });
      form.setValue(scoped('fiscalPower'), null, { shouldDirty: true, shouldValidate: true });
      form.setValue(scoped('numberOfSeats'), null, { shouldDirty: true, shouldValidate: true });
      const set = (key: VehicleIdentityFieldKey, value?: string | null) => {
        if (value == null) return;
        form.setValue(scoped(key), value, { shouldDirty: true, shouldValidate: true });
      };
      set('registrationNumber', vehicle.registrationNumber);
      set('brand', vehicle.brand);
      set('model', vehicle.model);
      set('firstCirculationDate', vehicle.firstRegistrationDate);
      onVehicleFound?.(vehicle);
    },
    [form, scoped, onVehicleFound],
  );

  const handleVehicleFound = useCallback(
    (vehicle: SivVehicle) => {
      setPlateNotFound(false);
      applyVehicle(vehicle);
    },
    [applyVehicle],
  );

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    if (!field.manualFlagField) return;
    const name = scoped(field.manualFlagField);
    const isManual = activeTab === 'manual';
    if ((form.getValues(name) === true) !== isManual) {
      form.setValue(name, isManual, { shouldDirty: true });
    }
  }, [activeTab, field.manualFlagField, form, scoped]);

  // Brand changed in the SRA picker → clear the now-stale model + version (brandModel tab only).
  const prevRef = useRef<{ brand?: string; model?: string }>({});
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { brand: working.brand, model: working.model };
    if (activeTab !== 'brandModel') return;
    if (prev.brand !== undefined && prev.brand !== working.brand && prev.model === working.model) {
      form.setValue(scoped('model'), '', { shouldDirty: true });
      form.setValue(scoped('version'), '', { shouldDirty: true });
    }
  }, [activeTab, working.brand, working.model, form, scoped]);

  // Drive the resolution gate. Guard on the current value: `form` is a fresh ref each render, so an
  // unconditional setValue({shouldValidate}) would loop (re-validate → re-render).
  useEffect(() => {
    const name = scoped(VEHICLE_SELECTION_RESOLVED_FIELD);
    const next = !showCard;
    if (form.getValues(name) !== next) {
      form.setValue(name, next, { shouldValidate: true });
    }
  }, [showCard, form, scoped]);

  const handleReplace = () => setContextVehicle(takeSnapshot());

  const handleKeepCurrent = () => {
    if (!contextVehicle) return;
    [...identityFields, ...SNAPSHOT_EXTRA_KEYS].forEach(key => {
      const isNumber = (SNAPSHOT_EXTRA_KEYS as readonly string[]).includes(key);
      form.setValue(scoped(key), contextVehicle[key] ?? (isNumber ? null : ''), {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const onTabChange = (value: string) => {
    // The manually typed vehicle becomes the reference for the next tab
    if (isManualTab) setContextVehicle(null);
    setActiveTab(value as VehicleInputTab);
    setPlateNotFound(false);
  };

  const comparisonRows: Array<VehicleComparisonRow> = identityFields.map(key => ({
    key,
    label: t(`fields.${key}.label`),
    current: formatIdentityValue(key, contextVehicle?.[key]),
    proposed: formatIdentityValue(key, working[key]),
  }));

  const renderDateField = (name: string, label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field: dateField }) => (
        <FormItem>
          <FormLabel>
            <span className="flex">{label}</span>
          </FormLabel>
          <FormControl>
            <VehicleDateInput field={dateField} />
          </FormControl>
          <FormDescription>{t('vehicleInput.firstCirculationDate.help')}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  // Date drives the version list (SRA filters by it), so date first and version only once a date is
  // set. Apps with the date outside the composite (e.g. Garantie Plus) show version once model is known.
  const renderVersionAndDate = () => {
    const dateInComposite = identityFields.includes('firstCirculationDate');
    const showVersion =
      Boolean(working.model) &&
      identityFields.includes('version') &&
      (!dateInComposite || Boolean(working.firstCirculationDate));
    return (
      <>
        {working.model &&
          dateInComposite &&
          renderDateField(scoped('firstCirculationDate'), t('fields.firstCirculationDate.label'))}

        {showVersion && (
          <LabeledField name={scoped('version')} label={t('fields.version.label')}>
            <VehicleVersionFormField
              watchedFieldNames={{
                version: scoped('version'),
                brand: scoped('brand'),
                model: scoped('model'),
                fiscalPower: scoped('fiscalPower'),
                numberOfSeats: scoped('numberOfSeats'),
                firstCirculationDate: scoped('firstCirculationDate'),
                newValue: scoped('vehicleNewValue'),
              }}
            />
          </LabeledField>
        )}
      </>
    );
  };

  return (
    <div>
      {bannerVehicle && (
        <div className="mb-4 rounded-lg border bg-muted/40 p-4">
          <Group gap={2} className="mb-3">
            <CarIcon className="h-4 w-4 text-primary" />
            <Text
              inline
              size="xs"
              weight="semibold"
              tone="primary"
              className="uppercase tracking-wide"
            >
              {t('vehicleInput.currentVehicle')}
            </Text>
          </Group>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            {DEFAULT_VEHICLE_IDENTITY_FIELDS.filter(key => identityFields.includes(key)).map(
              key => (
                <Stack key={key} gap={0} className="gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t(`fields.${key}.label`)}</dt>
                  <dd className="font-medium">
                    {formatIdentityValue(key, (bannerVehicle as Snapshot)[key]) || '—'}
                  </dd>
                </Stack>
              ),
            )}
          </dl>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="w-full">
          {tabs.includes('plate') && (
            <TabsTrigger value="plate" className="flex-1">
              {t('vehicleInput.tabs.plate')}
            </TabsTrigger>
          )}
          {tabs.includes('brandModel') && (
            <TabsTrigger value="brandModel" className="flex-1">
              {t('vehicleInput.tabs.brandModel')}
            </TabsTrigger>
          )}
          {tabs.includes('manual') && (
            <TabsTrigger value="manual" className="flex-1">
              {t('vehicleInput.tabs.manual')}
            </TabsTrigger>
          )}
        </TabsList>

        {tabs.includes('plate') && (
          <TabsContent value="plate">
            <LabeledField
              name={scoped('registrationNumber')}
              label={t('fields.registrationNumber.label')}
              required
            >
              <VehicleRegistrationNumberInput
                managedExternally
                onVehicleFound={handleVehicleFound}
                onNotFound={() => setPlateNotFound(true)}
              />
            </LabeledField>

            {plateNotFound && (
              <Alert variant="warning" className="mt-2">
                <AlertDescription>
                  {t('vehicleInput.plate.notFound')}{' '}
                  {tabs.includes('brandModel') && (
                    <button
                      type="button"
                      className="cursor-pointer font-medium text-primary underline"
                      onClick={() => onTabChange('brandModel')}
                    >
                      {t('vehicleInput.plate.switchToBrandModel')}
                    </button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {(working.brand || working.model) && (
              <Stack className="mt-4">
                {/* Brand and model shown as separate required fields so a model missing from
                    another tab surfaces its validation error here too. */}
                <LabeledField name={scoped('brand')} label={t('fields.brand.label')} required>
                  <Input disabled {...form.register(scoped('brand'))} />
                </LabeledField>
                <LabeledField name={scoped('model')} label={t('fields.model.label')} required>
                  <Input disabled {...form.register(scoped('model'))} />
                </LabeledField>
                {renderVersionAndDate()}
              </Stack>
            )}
          </TabsContent>
        )}

        {tabs.includes('brandModel') && (
          <TabsContent value="brandModel" className="flex flex-col gap-4">
            <LabeledField
              name={scoped('registrationNumber')}
              label={t('vehicleInput.brandModel.optionalPlate')}
            >
              <Input
                {...form.register(scoped('registrationNumber'))}
                placeholder={t('vehicleInput.brandModel.optionalPlatePlaceholder')}
              />
            </LabeledField>

            <LabeledField name={scoped('brand')} label={t('fields.brand.label')} required>
              <VehicleBrandFormField
                field={{ name: scoped('brand'), type: 'vehicleBrand', required: true }}
              />
            </LabeledField>

            {working.brand && (
              <LabeledField name={scoped('model')} label={t('fields.model.label')} required>
                <VehicleModelFormField
                  field={{ name: scoped('model'), type: 'vehicleModel', required: true }}
                  watchedFieldNames={{ brand: scoped('brand') }}
                  disableAutoClear
                />
              </LabeledField>
            )}

            {renderVersionAndDate()}
          </TabsContent>
        )}

        {tabs.includes('manual') && (
          <TabsContent value="manual" className="flex flex-col gap-4">
            <Alert variant="warning">
              <AlertDescription>{t('vehicleInput.manual.info')}</AlertDescription>
            </Alert>
            {identityFields.map(key =>
              key === 'firstCirculationDate' ? (
                <div key={key}>
                  {renderDateField(scoped(key), t('fields.firstCirculationDate.label'))}
                </div>
              ) : (
                <LabeledField
                  key={key}
                  name={scoped(key)}
                  label={t(`fields.${key}.label`)}
                  required={key !== 'registrationNumber' || !!field.registrationNumberRequired}
                >
                  <Input {...form.register(scoped(key))} />
                </LabeledField>
              ),
            )}
          </TabsContent>
        )}
      </Tabs>

      {showCard && (
        <VehicleComparisonCard
          rows={comparisonRows}
          onKeepCurrent={handleKeepCurrent}
          onReplace={handleReplace}
          showBlockedMessage={suivantBlocked}
        />
      )}
    </div>
  );
}
